import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateMessageIsRead from "@insite/client-framework/Store/Data/Messages/Handlers/UpdateMessageIsRead";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadMessages from "@tJohnstonCompanies/Store/Data/Messages/Handlers/LoadMessage";
import { getMessagesDataView } from "@tJohnstonCompanies/Store/Data/Messages/MessagesSelector";
import sortBy from "lodash/sortBy";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    orderNumber: state.pages.orderDetails.orderNumber,
    messagesDataView: getMessagesDataView(state, { orderNumber: state.pages.orderDetails.orderNumber }),

    loadMessages,
    styles,
});

const mapDispatchToProps = {
    updateMessageIsRead,
    loadMessages,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistoryDetailsMessageStyles {
    mainWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    messageTextArea?: TextAreaProps;
    sendButton?: ButtonPresentationProps;
    messagesContainer?: GridContainerProps;
    messageGridItem?: GridItemProps;
    userNameText?: TypographyPresentationProps;
    messageDateText?: TypographyPresentationProps;
    messageBodyText?: TypographyPresentationProps;
}
export const orderHistoryDetailsMessageStyles: OrderHistoryDetailsMessageStyles = {
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            background-color: ${getColor("common.accent")};
            padding: 10px 20px;
        `,
    },
    titleText: {
        variant: "h3",
    },
    messageTextArea: {
        cssOverrides: {
            formField: css`
                margin-bottom: 20px;
            `,
        },
    },
    messagesContainer: {
        gap: 20,
        css: css`
            ${GridOffset} {
                margin-top: 20px;
            }
            ${GridItemStyle} {
                margin: 0 10px;
                padding: 10px 0;
                border-top: 1px solid ${getColor("common.border")};
            }
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    userNameText: {
        weight: "bold",
    },
    messageBodyText: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = orderHistoryDetailsMessageStyles;

function filterMessagesBasedOnFilterType(messages: MessageModel[], messagesFilterType: string): MessageModel[] {
    return messages.filter(message => {
        if (messagesFilterType === "All") {
            return true;
        }

        if (messagesFilterType === "Unread") {
            return !message.isRead;
        }

        return message.isRead;
    });
}

const OrderHistoryDetailsMessage: FC<Props> = ({ orderNumber, messagesDataView, styles, loadMessages }) => {
    useEffect(() => {
        if (!messagesDataView.value) {
            loadMessages({ orderNumber });
        }
    }, [orderNumber]);
    const messages = sortBy(messagesDataView.value ? messagesDataView.value : [], ["createdDate"]);

    return (
        <GridContainer {...styles.messagesContainer}>
            {messages.map(message => (
                <GridItem key={message.dateToDisplay.toString()} {...styles.messageGridItem}>
                    <Typography {...styles.userNameText}>{message.displayName}</Typography>
                    <Typography {...styles.messageDateText}>
                        <LocalizedDateTime
                            dateTime={message.dateToDisplay}
                            options={{
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                            }}
                        />
                    </Typography>
                    <Typography {...styles.messageBodyText}>{message.body}</Typography>
                </GridItem>
            ))}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderHistoryDetailsMessage);
